import {isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

export type AdditionalPropertiesProps = {
  requirementError: StringUnion;
  premiumWaiver: StringUnion;
  strikeLayOff: StringUnion;
  applicantOccupationalClass: StringUnion;
  applicantSpouseOccupationalClass: StringUnion;
  eliminationPeriod: StringUnion;
  benefitPeriod: number;
  trueAge: number;
  mib?: boolean;
  accidentalDeathBenefit: StringUnion;
  adbUnits: number;
  occClass?: StringUnion;
  mentalNervousExclusion?: StringUnion;
};

export class UnvalidatedAdditionalProperties {
  public readonly key = 'UnvalidatedAdditionalProperties';

  constructor(
    public readonly requirementError: StringUnion,
    public readonly premiumWaiver: StringUnion,
    public readonly strikeLayOff: StringUnion,
    public readonly applicantOccupationalClass: StringUnion,
    public readonly applicantSpouseOccupationalClass: StringUnion,
    public readonly eliminationPeriod: StringUnion,
    public readonly benefitPeriod: number,
    public readonly trueAge: number,
    public readonly accidentalDeathBenefit: StringUnion,
    public readonly adbUnits: number,
    public readonly mib?: boolean,
    public readonly occClass?: StringUnion,
    public readonly mentalNervousExclusion?: StringUnion
  ) {}
}

export class InvalidAdditionalProperties {
  public readonly key = 'InvalidAdditionalProperties';

  constructor(
    public readonly requirementError: StringUnion,
    public readonly premiumWaiver: StringUnion,
    public readonly strikeLayOff: StringUnion,
    public readonly applicantOccupationalClass: StringUnion,
    public readonly applicantSpouseOccupationalClass: StringUnion,
    public readonly eliminationPeriod: StringUnion,
    public readonly accidentalDeathBenefit: StringUnion,
    public readonly adbUnits: number,
    public readonly benefitPeriod: number,
    public readonly trueAge: number,
    public readonly mib?: boolean,
    public readonly occClass?: StringUnion,
    public readonly mentalNervousExclusion?: StringUnion
  ) {}
}

export class ValidAdditionalProperties {
  public static createFromApp({
    requirementError,
    premiumWaiver,
    strikeLayOff,
    applicantOccupationalClass,
    applicantSpouseOccupationalClass,
    eliminationPeriod,
    benefitPeriod,
    trueAge,
    accidentalDeathBenefit,
    adbUnits,
    mib,
    occClass,
  }: AdditionalPropertiesProps) {
    return this.create(
      requirementError,
      premiumWaiver,
      strikeLayOff,
      applicantOccupationalClass,
      applicantSpouseOccupationalClass,
      eliminationPeriod,
      benefitPeriod,
      trueAge,
      accidentalDeathBenefit,
      adbUnits,
      mib,
      occClass
    );
  }

  public static create(
    requirementError: StringUnion,
    premiumWaiver: StringUnion,
    strikeLayOff: StringUnion,
    applicantOccupationalClass: StringUnion,
    applicantSpouseOccupationalClass: StringUnion,
    eliminationPeriod: StringUnion,
    benefitPeriod: number,
    trueAge: number,
    accidentalDeathBenefit: StringUnion,
    adbUnits: number,
    mib?: boolean,
    occClass?: StringUnion,
    mentalNervousExclusion?: StringUnion
  ) {
    return isNonEmptyString(requirementError)
      ? new ValidAdditionalProperties(
          requirementError,
          premiumWaiver,
          strikeLayOff,
          applicantOccupationalClass,
          applicantSpouseOccupationalClass,
          eliminationPeriod,
          benefitPeriod,
          trueAge,
          accidentalDeathBenefit,
          adbUnits,
          mib,
          occClass,
          mentalNervousExclusion
        )
      : new InvalidAdditionalProperties(
          requirementError,
          premiumWaiver,
          strikeLayOff,
          applicantOccupationalClass,
          applicantSpouseOccupationalClass,
          eliminationPeriod,
          accidentalDeathBenefit,
          adbUnits,
          benefitPeriod,
          trueAge,
          mib,
          occClass,
          mentalNervousExclusion
        );
  }

  public readonly key = 'ValidAdditionalProperties';
  private constructor(
    public readonly requirementError: StringUnion,
    public readonly premiumWaiver: StringUnion,
    public readonly strikeLayOff: StringUnion,
    public readonly applicantOccupationalClass: StringUnion,
    public readonly applicantSpouseOccupationalClass: StringUnion,
    public readonly eliminationPeriod: StringUnion,
    public readonly benefitPeriod: number,
    public readonly trueAge: number,
    public readonly accidentalDeathBenefit: StringUnion,
    public readonly adbUnits: number,
    public readonly mib?: boolean,
    public readonly occClass?: StringUnion,
    public readonly mentalNervousExclusion?: StringUnion
  ) {}
}

export type AdditionalProperties =
  | UnvalidatedAdditionalProperties
  | InvalidAdditionalProperties
  | ValidAdditionalProperties;

export type AdditionalPropertiesOptions<TReturn> = ToPartialMatchOptions<AdditionalProperties, 'key', TReturn>;

export const matchPlanCodes = <TReturn>(options: AdditionalPropertiesOptions<TReturn>) => (
  app: AdditionalProperties
): TReturn => {
  switch (app.key) {
    case 'InvalidAdditionalProperties':
      return options.InvalidAdditionalProperties ? options.InvalidAdditionalProperties(app) : options.default(app);
    case 'UnvalidatedAdditionalProperties':
      return options.UnvalidatedAdditionalProperties
        ? options.UnvalidatedAdditionalProperties(app)
        : options.default(app);
    case 'ValidAdditionalProperties':
      return options.ValidAdditionalProperties ? options.ValidAdditionalProperties(app) : options.default(app);
  }
};

export const EmptyAdditionalProperties = new InvalidAdditionalProperties(
  NonEmptyString.create('NO'),
  NonEmptyString.create('NO'),
  NonEmptyString.create('NO'),
  NonEmptyString.create(''),
  NonEmptyString.create(''),
  NonEmptyString.create(''),
  NonEmptyString.create(''),
  0,
  0,
  0
);

export const changeMentalNervousExclusion = (mentalNervousExclusion: string) => (app: AdditionalProperties) =>
  ValidAdditionalProperties.createFromApp({...app, mentalNervousExclusion: NonEmptyString.create(mentalNervousExclusion)});

export const changeOccClass = (occClass: string) => (app: AdditionalProperties) =>
  ValidAdditionalProperties.createFromApp({...app, occClass: NonEmptyString.create(occClass)});

export const changeRequirementError = (requirementError: string) => (app: AdditionalProperties) =>
  ValidAdditionalProperties.createFromApp({...app, requirementError: NonEmptyString.create(requirementError)});

export const changePremiumWaiver = (premiumWaiver: string) => (app: AdditionalProperties) =>
  ValidAdditionalProperties.createFromApp({...app, premiumWaiver: NonEmptyString.create(premiumWaiver)});

export const changeStrikeLayOff = (strikeLayoff: string) => (app: AdditionalProperties) =>
  ValidAdditionalProperties.createFromApp({...app, strikeLayOff: NonEmptyString.create(strikeLayoff)});

export const changeApplicantOccupationalClass = (applicantOccupationalClass: string) => (app: AdditionalProperties) =>
  ValidAdditionalProperties.createFromApp({
    ...app,
    applicantOccupationalClass: NonEmptyString.create(applicantOccupationalClass),
  });

export const changeApplicantSpouseOccupationalClass = (applicantSpouseOccupationalClass: string) => (
  app: AdditionalProperties
) =>
  ValidAdditionalProperties.createFromApp({
    ...app,
    applicantSpouseOccupationalClass: NonEmptyString.create(applicantSpouseOccupationalClass),
  });

export const changeEliminationPeriod = (eliminationPeriod: string) => (app: AdditionalProperties) =>
  ValidAdditionalProperties.createFromApp({...app, eliminationPeriod: NonEmptyString.create(eliminationPeriod)});

export const changeBenefitPeriod = (benefitPeriod: number) => (app: AdditionalProperties) =>
  ValidAdditionalProperties.createFromApp({
    ...app,
    benefitPeriod,
  });

export const changeTrueAge = (trueAge: number) => (app: AdditionalProperties) =>
  ValidAdditionalProperties.createFromApp({
    ...app,
    trueAge,
  });

export const changeMib = (mib?: boolean) => (app: AdditionalProperties) =>
  ValidAdditionalProperties.createFromApp({...app, mib});
